import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useApiConfig } from "common-fe";
import { PermissionsApi, RolesApi, EntitlementsApi, Configuration } from "account-common";
import { jwtDecode } from "jwt-decode";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

const parseMap = (claim) => {
  if (!claim) return {};
  if (typeof claim === "object") return claim;
  try {
    return JSON.parse(claim);
  } catch {
    return {};
  }
};

// Active app names from an org's stored entitlement records — mirrors the
// backend's activeEntitlementApps (account-api/src/lib/entitlements.js), the
// same active-only filter PreTokenGeneration uses to stamp the token claim.
const activeEntitlementApps = (entitlements) =>
  (Array.isArray(entitlements) ? entitlements : [])
    .map((e) => (typeof e === "string" ? { app: e, status: "active" } : e))
    .filter((e) => e && e.app && e.status === "active")
    .map((e) => e.app);

/**
 * Data for the escalation-aware member role UI (AUTHZ.md §9.2 / §5.7):
 *  - the permission manifests (role titles/descriptions + granted permissions),
 *    via PermissionsApi.getPermissionsManifest,
 *  - the active org's entitled apps + the ACTOR's own roles, decoded from the
 *    access token (the per-org maps the API resolves) — this is JWT inspection,
 *    not a network call — used to decide which roles the actor may grant,
 *  - assignRole / revokeRole via RolesApi.
 *
 * All network access goes through the generated OpenAPI client (no direct fetch).
 *
 * @param {string} orgId - the org whose member is being managed
 */
export function useMemberRolesData(orgId) {
  const auth = useAuth();
  const buildConfig = useApiConfig();
  const accessToken = auth.user?.access_token;

  const [manifests, setManifests] = useState({});
  // The managed org's entitled apps as read from its RECORD (super path); null
  // until fetched. See the effect + entitledApps note below.
  const [orgEntitledApps, setOrgEntitledApps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = useCallback(() => {
    if (!accessToken) return null;
    return new Configuration(buildConfig(ACCOUNT_API_BASE_URL));
  }, [accessToken, buildConfig]);

  // Actor context for the managed org, from the token (JWT decode, not a fetch).
  let actorRoles = [];
  let tokenEntitledApps = [];
  let isSuper = false;
  if (accessToken) {
    try {
      const claims = jwtDecode(accessToken);
      // Access token carries `super`; ID token carries `custom:super`. Check both.
      const superClaim = claims["custom:super"] ?? claims.super;
      isSuper = superClaim === "true" || superClaim === true;
      actorRoles = parseMap(claims.roles)[orgId] || [];
      tokenEntitledApps = parseMap(claims.entitlements)[orgId] || [];
    } catch {
      /* keep defaults */
    }
  }

  useEffect(() => {
    const cfg = config();
    if (!cfg) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [manifestRes, orgEnt] = await Promise.all([
          new PermissionsApi(cfg).getPermissionsManifest(),
          // The token only carries entitlements for the actor's MEMBERSHIP orgs
          // (PreTokenGeneration), so a super managing an org they don't belong to
          // has none — hiding that org's app role groups. Read the org's own
          // entitlements (super-only endpoint) so the managed org, not the actor,
          // decides which app role groups show. Non-supers keep the token path.
          // Non-fatal: on failure fall back to the token (null) rather than
          // breaking the whole dialog (the manifest fetch is the fatal one).
          isSuper
            ? new EntitlementsApi(cfg).getOrgEntitlements(orgId).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setManifests(manifestRes.data?.data?.manifests || {});
        if (orgEnt) {
          setOrgEntitledApps(activeEntitlementApps(orgEnt.data?.data?.entitlements));
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config, orgId, isSuper]);

  // The managed org's entitled apps decide which app role groups render. For a
  // super, use the org record (fetched above) — its token lacks the org; fall
  // back to the token if the fetch hasn't resolved. Non-supers use the token,
  // which is authoritative for their own membership orgs.
  const entitledApps =
    isSuper && orgEntitledApps !== null ? orgEntitledApps : tokenEntitledApps;

  const assignRole = useCallback(
    async (userId, role) => {
      const res = await new RolesApi(config()).assignRole(orgId, userId, { role });
      return res.data?.data?.roles;
    },
    [config, orgId]
  );

  const revokeRole = useCallback(
    async (userId, role) => {
      const res = await new RolesApi(config()).revokeRole(orgId, userId, { role });
      return res.data?.data?.roles;
    },
    [config, orgId]
  );

  return { manifests, entitledApps, actorRoles, isSuper, loading, error, assignRole, revokeRole };
}

/**
 * Whether `actor` may grant `qualifiedRole` (mirrors the server's §5.7 rule;
 * the API re-checks regardless). Owner = highest; admin manages app roles only.
 */
export function canGrantRole({ actorRoles, isSuper }, qualifiedRole) {
  const isOwnerLevel = isSuper || actorRoles.includes("account:org_owner");
  const isAdminLevel = isOwnerLevel || actorRoles.includes("account:org_admin");
  if (qualifiedRole === "account:org_owner") return false; // never hand-assigned
  if (qualifiedRole === "account:org_admin") return isOwnerLevel; // owner only
  return isAdminLevel; // app roles + org_member
}
